import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react'

export default function VerifyEmail({ onVerified }) {
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState(null)

  useEffect(() => {
    const verify = async () => {
      try {
        // ✅ Get the session - this is where Supabase sets the session after email confirmation
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError) {
          setStatus('error')
          setError(sessionError.message)
          return
        }

        // ✅ If we have a session, the user is confirmed
        if (session?.user) {
          // Check if email is confirmed
          if (session.user.email_confirmed_at) {
            setStatus('success')
            // Redirect after 2 seconds
            setTimeout(() => {
              onVerified?.()
              window.location.href = '/'
            }, 2000)
            return
          }
        }

        // ✅ Check for email confirmation in URL hash (Supabase sends it here)
        const hashParams = new URLSearchParams(window.location.hash.replace('#', ''))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        const errorDesc = hashParams.get('error_description')

        if (errorDesc) {
          setStatus('error')
          setError(decodeURIComponent(errorDesc))
          return
        }

        if (accessToken && refreshToken) {
          // ✅ Set the session from the hash
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          })
          
          if (setSessionError) {
            setStatus('error')
            setError(setSessionError.message)
            return
          }

          // ✅ Get updated session
          const { data: { session: newSession } } = await supabase.auth.getSession()
          if (newSession?.user?.email_confirmed_at) {
            setStatus('success')
            setTimeout(() => {
              onVerified?.()
              window.location.href = '/'
            }, 2000)
            return
          }
        }

        // ✅ If we get here, the user might need to confirm email first
        setStatus('pending')
        
      } catch (err) {
        setStatus('error')
        setError(err.message)
      }
    }

    verify()
  }, [onVerified])

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-brand mx-auto" />
          <p className="mt-4 text-sm text-muted">Verifying your email...</p>
        </div>
      </div>
    )
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-ink">Email Verified! </h1>
          <p className="text-muted mt-2">Your account has been confirmed. Redirecting to dashboard...</p>
          <div className="mt-6 w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
            <div className="h-full bg-brand rounded-full animate-progress" style={{ width: '100%', animationDuration: '2000ms' }} />
          </div>
        </div>
      </div>
    )
  }

  // Pending state (email not confirmed yet)
  if (status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail size={32} className="text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-ink">Check Your Email</h1>
          <p className="text-sm text-muted mt-2">
            We've sent a confirmation link to your email. Please click the link to verify your account.
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
          >
            Back to Login <ArrowRight size={16} />
          </button>
        </div>
      </div>
    )
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <div className="bg-white border border-neutral-200 rounded-xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle size={32} className="text-rose-600" />
        </div>
        <h1 className="text-xl font-bold text-ink">Verification Failed</h1>
        <p className="text-sm text-muted mt-2">
          {error || 'We couldn\'t verify your email. The link may have expired.'}
        </p>
        <button 
          onClick={() => window.location.href = '/'}
          className="btn-primary w-full mt-6"
        >
          Back to Login
        </button>
      </div>
    </div>
  )
}